import useUiTranslation from "../../utils/useUiTranslation";
import './RequestForms.css';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import useCurrentUser from '../../hooks/useCurrentUser';
import { HelpTooltip } from '../../components/ui/HelpTooltip';
import { buildRequestSubmissionState } from '../../utils/requestSubmission';
import ProjectSelector from '../../components/projects/ProjectSelector';
import useRequestDraftAutosave from '../../hooks/useRequestDraftAutosave';
import UrgentRequestToggle from '../../components/requests/UrgentRequestToggle';

const MedicationRequestForm = () => {
  const uiText = useUiTranslation();
  const { t } = useTranslation();
  const [justification, setJustification] = useState('');
  const [items, setItems] = useState([{ item_name: '', dosage: '', quantity: 1 }]);
  const [itemErrors, setItemErrors] = useState([{}]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [projectId, setProjectId] = useState('');

  const { user, loading, error } = useCurrentUser();
  const targetDeptId = user?.department_id;
  const targetSectionId = user?.section_id;
  const isScm = useMemo(() => (user?.role || '').toLowerCase() === 'scm', [user?.role]);
  const navigate = useNavigate();

  const MAX_ATTACHMENT_SIZE_MB = 10;
  const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
  const allowedExtensions = useMemo(
    () => ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.xlsx'],
    []
  );

  const draftData = useMemo(
    () => ({
      items,
      justification,
      projectId,
    }),
    [items, justification, projectId]
  );

  const restoreDraft = useCallback((draft) => {
    if (typeof draft?.justification === 'string') setJustification(draft.justification);
    if (typeof draft?.projectId === 'string') setProjectId(draft.projectId);
    if (Array.isArray(draft?.items) && draft.items.length > 0) {
      setItems(
        draft.items.map((item) => ({ item_name: '', dosage: '', quantity: 1, ...item }))
      );
    }
  }, []);

  const {
    clearDraft,
    isSaving: isDraftSaving,
    lastSavedLabel,
  } = useRequestDraftAutosave({
    storageKey: 'medication_request_draft_v1',
    data: draftData,
    restoreDraft,
  });

  useEffect(() => {
    setItemErrors((prev) => {
      if (prev.length === items.length) return prev;
      return items.map((_, index) => prev[index] || {});
    });
  }, [items]);

  const medicationSummary = useMemo(() => {
    const totalQuantity = items.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
    const uniqueNames = new Set(
      items
        .map((item) => item.item_name.trim().toLowerCase())
        .filter(Boolean)
    );
    const missingDosage = items.some((item) => !item.dosage.trim());

    return {
      totalQuantity,
      uniqueCount: uniqueNames.size,
      itemCount: items.length,
      missingDosage,
    };
  }, [items]);

  const validateItemField = (field, value) => {
    if (field === 'item_name' && !value.trim()) {
      return 'Medication name is required.';
    }
    if (field === 'dosage' && !value.trim()) {
      return 'Please specify a dosage.';
    }
    if (field === 'quantity') {
      const qty = Number(value);
      if (!Number.isInteger(qty) || qty < 1) {
        return 'Quantity must be a whole number greater than 0.';
      }
    }
    return '';
  };

  const validateItem = (item) =>
    Object.fromEntries(
      ['item_name', 'dosage', 'quantity']
        .map((field) => {
          const error = validateItemField(field, item[field]);
          return error ? [field, error] : null;
        })
        .filter(Boolean)
    );

  const validateAttachmentList = (files) => {
    const errors = [];
    const validFiles = [];
    const allowedList = allowedExtensions.join(', ');

    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      if (!allowedExtensions.includes(ext)) {
        errors.push(`Unsupported file type ${ext}. Allowed: ${allowedList}`);
        return;
      }

      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        errors.push(
          `${file.name} exceeds ${MAX_ATTACHMENT_SIZE_MB}MB. Please upload a smaller file.`
        );
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, error: errors.join(' ') };
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = field === 'quantity' ? Number(value) || '' : value;
    setItems(updated);

    setItemErrors((prev) => {
      const next = [...prev];
      const existing = { ...(next[index] || {}) };
      const errorMessage = validateItemField(field, updated[index][field]);
      if (errorMessage) {
        existing[field] = errorMessage;
      } else {
        delete existing[field];
      }
      next[index] = existing;
      return next;
    });
  };

  const addItem = () => {
    setItems([...items, { item_name: '', dosage: '', quantity: 1 }]);
    setItemErrors((prev) => [...prev, {}]);
  };

  const duplicateItem = (index) => {
    setItems((prevItems) => {
      const next = [...prevItems];
      const duplicate = { ...next[index] };
      next.splice(index + 1, 0, duplicate);
      return next;
    });
    setItemErrors((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, { ...next[index] });
      return next;
    });
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    if (!window.confirm(t('medicationRequestForm.alerts.removeItem'))) return;
    setItems(items.filter((_, i) => i !== index));
    setItemErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentChange = (fileList) => {
    const files = Array.from(fileList || []);
    const { validFiles, error } = validateAttachmentList(files);
    setAttachments(validFiles);
    setAttachmentsError(error);
  };

  const handleRemoveAttachment = (attachmentIndex) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== attachmentIndex));
    setAttachmentsError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!justification.trim()) {
      alert(t('medicationRequestForm.alerts.justificationRequired'));
      return;
    }

    if (!targetDeptId) {
      alert(t('medicationRequestForm.alerts.missingDepartment'));
      return;
    }

    const itemValidationResults = items.map((item) => validateItem(item));
    const hasInvalidItem = itemValidationResults.some(
      (result) => Object.keys(result).length > 0
    );
    if (hasInvalidItem) {
      setItemErrors(itemValidationResults);
      alert(t('medicationRequestForm.alerts.invalidItems'));
      return;
    }

    if (attachmentsError) {
      alert(t('medicationRequestForm.alerts.attachments'));
      return;
    }

    const formData = new FormData();
    formData.append('request_type', 'Medication');
    formData.append('justification', justification);
    formData.append('budget_impact_month', '');
    formData.append('target_department_id', targetDeptId);
    formData.append('target_section_id', targetSectionId || '');
    formData.append('items', JSON.stringify(items));
    formData.append('is_urgent', isUrgent ? 'true' : 'false');
    formData.append('project_id', projectId);
    attachments.forEach((file) => formData.append('attachments', file));

    try {
      setIsSubmitting(true);
      const res = await api.post('/requests', formData);
      clearDraft();
      const state = buildRequestSubmissionState('Medication', res.data);
      navigate('/request-submitted', { state });
    } catch (err) {
      console.error('❌ Submission error:', err);
      alert(err.response?.data?.message || t('medicationRequestForm.alerts.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
          <div className="p-6 text-gray-600 text-center">{t('medicationRequestForm.loading')}</div>
      </>
    );
  }

  if (error || !user) {
    return (
      <>
          <div className="p-6 text-red-600 text-center">
          {t('medicationRequestForm.loadUserFailed')}
        </div>
      </>
    );
  }

  if (!user.can_request_medication && !isScm) {
    return (
      <>
        <div className="p-6 text-red-600 text-center">
          ❌ You are not authorized to submit medication requests.
        </div>
      </>
    );
  }

  return (
    <>
      <div className="request-form-page request-form-medication">
        <h1 className="text-2xl font-bold mb-4">
          {t('medicationRequestForm.title')}
          <HelpTooltip text={t('medicationRequestForm.help')} />
        </h1>
        <p className="request-form-intro">{t('requestFormLayout.medicationIntro')}</p>

        <p className="mb-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" role="status">
          {isDraftSaving
            ? t('medicationRequestForm.savingDraft')
            : lastSavedLabel
              ? t('medicationRequestForm.draftSaved', { time: lastSavedLabel })
              : t('medicationRequestForm.draftActive')}
        </p>

        <form onSubmit={handleSubmit} className="request-form-content">
          <section className="request-form-panel request-details"><h2>{t('requestFormLayout.requestDetails')}</h2>
          <div>
            <label className="block font-semibold mb-1">{t('medicationRequestForm.fields.department')}</label>
            <p className="p-2 border rounded bg-gray-100">{user.department_name}</p>
          </div>
          <div>
            <label className="block font-semibold mb-1">{t('medicationRequestForm.fields.section')}</label>
            <p className="p-2 border rounded bg-gray-100">{user.section_name || uiText("N/A")}</p>
          </div>
          <div>
            <label htmlFor="MedicationRequestForm-justification" className="block font-semibold mb-1">{t('medicationRequestForm.fields.justification')}</label>
            <textarea id="MedicationRequestForm-justification"
              className="w-full p-2 border rounded"
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t('medicationRequestForm.fields.justificationPlaceholder')}
              required
              disabled={isSubmitting}
            />
          </div>

          <ProjectSelector
            value={projectId}
            onChange={setProjectId}
            disabled={isSubmitting}
            user={user}
          />
          </section>
          <div className="request-form-panel request-items">
            <div className="flex items-center justify-between gap-2 mb-2">
              <label className="block font-semibold">{t('medicationRequestForm.fields.medications')}</label>
              <span className="text-sm text-gray-500">
                {t('medicationRequestForm.fields.summary', { count: medicationSummary.itemCount, quantity: medicationSummary.totalQuantity })}
              </span>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-4">
              <p className="font-semibold">{t('medicationRequestForm.fields.quickSummary')}</p>
              <p>
                {medicationSummary.uniqueCount} unique medication
                {medicationSummary.uniqueCount === 1 ? '' : 's'} requested.
              </p>
              <p
                className={
                  medicationSummary.missingDosage
                    ? 'text-red-700 font-medium'
                    : 'text-blue-900'
                }
              >
                {medicationSummary.missingDosage
                  ? 'At least one entry is missing a dosage specification.'
                  : 'All medications include dosage details.'}
              </p>
            </div>
            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="request-item-card space-y-3"
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-gray-700">{uiText("Medication")}{" "}{index + 1}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => duplicateItem(index)}
                        className="text-blue-600 hover:underline disabled:text-gray-400"
                        disabled={isSubmitting}
                      >{uiText("Duplicate")}</button>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:underline disabled:text-gray-400"
                          disabled={isSubmitting}
                        >{uiText("Remove")}</button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <input
                        type="text"
                        placeholder={t('medicationRequestForm.fields.namePlaceholder')}
                        aria-label={`Item ${index + 1} Name`}
                        value={item.item_name}
                        onChange={(e) =>
                          handleItemChange(index, 'item_name', e.target.value)
                        }
                        className={`w-full p-2 border rounded ${
                          itemErrors[index]?.item_name ? 'border-red-400' : ''
                        }`}
                        required
                        disabled={isSubmitting}
                      />
                      {itemErrors[index]?.item_name && (
                        <p className="mt-1 text-sm text-red-600">
                          {itemErrors[index].item_name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <input
                        type="text"
                        placeholder={t('medicationRequestForm.fields.dosagePlaceholder')}
                        aria-label={`Item ${index + 1} Dosage`}
                        value={item.dosage}
                        onChange={(e) =>
                          handleItemChange(index, 'dosage', e.target.value)
                        }
                        className={`w-full p-2 border rounded ${
                          itemErrors[index]?.dosage ? 'border-red-400' : ''
                        }`}
                        disabled={isSubmitting}
                      />
                      {itemErrors[index]?.dosage && (
                        <p className="mt-1 text-sm text-red-600">
                          {itemErrors[index].dosage}
                        </p>
                      )}
                    </div>
                    <div className="w-full sm:w-32">
                      <input
                        type="number"
                        min={1}
                        aria-label={`Item ${index + 1} Quantity`}
                        value={item.quantity}
                        onChange={(e) =>
                          handleItemChange(index, 'quantity', e.target.value)
                        }
                        className={`w-full p-2 border rounded ${
                          itemErrors[index]?.quantity ? 'border-red-400' : ''
                        }`}
                        required
                        disabled={isSubmitting}
                      />
                      {itemErrors[index]?.quantity && (
                        <p className="mt-1 text-sm text-red-600">
                          {itemErrors[index].quantity}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="request-add-item"
              disabled={isSubmitting}
            >
              + Add Another Medication
            </button>
          </div>
          <div className="request-form-panel">
            <label className="block font-semibold mb-1">{uiText("Attachments")}<HelpTooltip text={t('medicationRequestForm.uploadHelp')} />
            </label>
            <input
              type="file"
              multiple
              onChange={(e) => handleAttachmentChange(e.target.files)}
              className="p-2 border rounded w-full"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('medicationRequestForm.accepted', { types: allowedExtensions.join(', ') })}
            </p>
            {attachmentsError && (
              <p className="mt-1 text-sm text-red-600">{attachmentsError}</p>
            )}
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm">
                {attachments.map((file, index) => (
                  <li
                    key={file.name + index}
                    className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2"
                  >
                    <span className="truncate pr-3">{file.name}</span>
                    <button
                      type="button"
                      className="text-red-600 hover:underline disabled:text-gray-400"
                      onClick={() => handleRemoveAttachment(index)}
                      disabled={isSubmitting}
                    >{uiText("Remove")}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <UrgentRequestToggle
            user={user}
            checked={isUrgent}
            onChange={setIsUrgent}
            disabled={isSubmitting}
          />

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? t('medicationRequestForm.fields.submitting') : t('medicationRequestForm.fields.submit')}
              <HelpTooltip text={t('medicationRequestForm.submitHelp')} />
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default MedicationRequestForm;